import { parseBoothNumbers, normalizeBoothNumbers, formatBoothNumbersForApi, isBoothSelectType } from './boothNumbers';

/**
 * 将接口返回的 benefitList 转换为前端状态。
 * - 普通权益按 conferenceBenefitId 放入 selectedBenefits（Map 结构）
 * - 论坛权益（hallsId 非空 且 type=4）按论坛分组放入 forumBenefits 数组
 * @param {Array} benefitList 接口返回的权益列表
 * @param {Array} allBenefits 全部会议权益池（用于补充元数据）
 * @returns {{ selectedBenefits: Object, forumBenefits: Array }}
 */
export function transformBenefitListToState(benefitList, allBenefits) {
    const selectedBenefits = {};
    const forumBenefitsMap = {};

    if (!benefitList || !benefitList.length) return { selectedBenefits, forumBenefits: [] };

    let addedAtCounter = 0;
    for (const item of benefitList) {
        const poolItem = allBenefits.find(b => b.id === item.conferenceBenefitId);
        let availableBoothNumbers = [];
        if (poolItem?.boothNumbers) {
            try {
                availableBoothNumbers = JSON.parse(poolItem.boothNumbers);
            } catch (_) {}
        }

        const benefitState = {
            conferenceBenefitId: item.conferenceBenefitId,
            benefitId: item.benefitId,
            type: poolItem?.type || 0,
            benefitName: poolItem?.benefitName || '',
            boothNumbers: normalizeBoothNumbers(item.boothNumbers),
            /** 编辑回显时本机构已保存展位，用于区分 soldBoothList 中本机构与其他机构占用 */
            originalBoothNumbers: normalizeBoothNumbers(item.boothNumbers),
            availableBoothNumbers,
            isBoothBenefit: poolItem?.isBoothBenefit,
            builtin: poolItem?.builtin,
            benefitDesc: poolItem?.benefitDesc || '',
            priceReference: poolItem?.priceReference || '',
            totalQuantity: poolItem?.quantity ?? null,
            soldCount: poolItem?.soldCount ?? null,
            soldBoothList: poolItem?.soldBoothList || [],
            quantity: item.quantity,
            remark: item.remark || '',
            hallsId: item.hallsId || null,
            id: item.id || null,
            sponsorId: item.sponsorId || null,
            realizationFiles: item.realizationFiles ?? '',
            /** 仅前端使用：用于稳定渲染顺序，提交时不会传给后端 */
            __addedAt: addedAtCounter++,
        };

        if (item.hallsId && (poolItem?.type === 4 || !poolItem)) {
            if (!forumBenefitsMap[item.hallsId]) {
                forumBenefitsMap[item.hallsId] = { hallsId: item.hallsId, hallName: '', benefits: [], _key: `forum_${item.hallsId}` };
            }
            forumBenefitsMap[item.hallsId].benefits.push(benefitState);
        } else {
            selectedBenefits[item.conferenceBenefitId] = benefitState;
        }
    }

    return { selectedBenefits, forumBenefits: Object.values(forumBenefitsMap) };
}

/**
 * 计算单条权益提交用 quantity：
 * - 展位类（isBoothBenefit=1）：取已选展位数量
 * - 普通权益：直接取用户填写的 quantity
 * @param {Object} b 权益 state 对象
 * @returns {number}
 */
function resolveQuantity(b) {
    if (isBoothSelectType(b)) {
        return parseBoothNumbers(b.boothNumbers).length;
    }
    return b.quantity || 0;
}

/**
 * 组装提交用 benefitList 数组，合并普通权益与论坛权益。
 * @param {Object} selectedBenefits 普通权益 state Map
 * @param {Array}  forumBenefits    论坛权益列表
 * @param {number} conferenceId     会议 ID
 * @param {number} orgId            机构 ID
 * @param {number} [sponsorId]      赞助商 ID（编辑时透传）
 * @returns {Array}
 */
export function assembleBenefitListPayload(selectedBenefits, forumBenefits, conferenceId, orgId, sponsorId) {
    const list = [];

    for (const b of Object.values(selectedBenefits)) {
        list.push({
            conferenceBenefitId: b.conferenceBenefitId,
            benefitId: b.benefitId,
            quantity: resolveQuantity(b),
            boothNumbers: formatBoothNumbersForApi(b.boothNumbers),
            remark: b.remark || '',
            hallsId: b.hallsId || 0,
            conferenceId,
            orgId,
            ...(b.id ? { id: b.id } : {}),
            ...(sponsorId || b.sponsorId ? { sponsorId: sponsorId || b.sponsorId } : {}),
        });
    }

    for (const forum of forumBenefits) {
        if (!forum.benefits) continue;
        for (const b of forum.benefits) {
            list.push({
                conferenceBenefitId: b.conferenceBenefitId,
                benefitId: b.benefitId,
                quantity: resolveQuantity(b),
                boothNumbers: formatBoothNumbersForApi(b.boothNumbers),
                remark: b.remark || '',
                hallsId: forum.hallsId || 0,
                conferenceId,
                orgId,
                ...(b.id ? { id: b.id } : {}),
                ...(sponsorId || b.sponsorId ? { sponsorId: sponsorId || b.sponsorId } : {}),
            });
        }
    }

    return list;
}
